import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, ShieldCheck, Search, Mail, Phone, Send } from 'lucide-react';
import API_URL from '@/config/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const SECTION_LABEL = { probound: 'ProBound', proepoxy: 'ProEpoxy', prodecor: 'ProDecor' };
const SECTION_COLOR = { probound: '#2F5E96', proepoxy: '#7A0B7A', prodecor: '#D4A24B' };

const InstallerCard = ({ installer, onContact }) => (
  <Card data-testid={`directory-card-${installer.user_id}`} className="overflow-hidden hover:shadow-lg transition-shadow">
    <div className="p-5">
      <div className="flex items-start gap-4">
        {installer.has_photo ? (
          <img src={`${API_URL}/api/public/profile-photo/${installer.user_id}`} alt={installer.name} className="w-16 h-16 rounded-full object-cover" />
        ) : installer.has_logo ? (
          <img src={`${API_URL}/api/public/logo/${installer.user_id}`} alt={installer.company_name} className="w-16 h-16 rounded-lg object-contain bg-gray-50 p-1" />
        ) : (
          <div className="w-16 h-16 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold text-xl">
            {(installer.company_name || installer.name || '?').charAt(0).toUpperCase()}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-gray-900 truncate">{installer.company_name || installer.name}</h3>
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <MapPin className="w-3 h-3" /> {installer.town || 'UK'}
            {installer.distance_miles != null && <span> · <strong>{installer.distance_miles} mi</strong></span>}
          </div>
          {installer.years_in_business && (
            <div className="text-xs text-gray-500 mt-0.5">{installer.years_in_business} years in business</div>
          )}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-1">
        {(installer.sections || []).map(s => (
          <span key={s} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-white px-2 py-0.5 rounded-full" style={{ background: SECTION_COLOR[s] }}>
            <ShieldCheck className="w-3 h-3" /> {SECTION_LABEL[s]}
          </span>
        ))}
      </div>

      {installer.bio && (
        <p className="mt-3 text-sm text-gray-700 line-clamp-3">{installer.bio}</p>
      )}

      <div className="mt-4 flex justify-end">
        <Button data-testid={`directory-contact-${installer.user_id}`} onClick={() => onContact(installer)} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white">
          <Send className="w-3.5 h-3.5 mr-1" /> Get a Quote
        </Button>
      </div>
    </div>
  </Card>
);

const ContactModal = ({ installer, onClose }) => {
  const [form, setForm] = useState({
    customer_name: '', customer_email: '', customer_phone: '',
    customer_postcode: '', section: installer.sections?.[0] || 'probound',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!form.customer_name || !form.customer_email) return toast.error('Name and email required');
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/public/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, preferred_installer_id: installer.user_id }),
      });
      if (!res.ok) throw new Error('Failed');
      toast.success("Thanks — we've sent your request to Ox Resin who'll be in touch shortly.");
      onClose();
    } catch (e) {
      toast.error('Could not send. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <Card className="max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-bold mb-1">Request a quote</h2>
        <p className="text-sm text-gray-600 mb-4">Preferred installer: <strong>{installer.company_name || installer.name}</strong></p>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Your name</Label><Input data-testid="public-contact-name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} /></div>
            <div><Label>Postcode</Label><Input data-testid="public-contact-postcode" value={form.customer_postcode} onChange={(e) => setForm({ ...form, customer_postcode: e.target.value })} /></div>
            <div><Label>Email</Label><Input data-testid="public-contact-email" type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} /></div>
            <div><Label>Phone</Label><Input data-testid="public-contact-phone" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} /></div>
          </div>
          <div>
            <Label>Service</Label>
            <select data-testid="public-contact-section" value={form.section} onChange={(e) => setForm({ ...form, section: e.target.value })} className="w-full border rounded-lg px-3 py-2">
              {(installer.sections || []).map(s => <option key={s} value={s}>{SECTION_LABEL[s]}</option>)}
            </select>
          </div>
          <div>
            <Label>What&apos;s the project? (optional)</Label>
            <Textarea data-testid="public-contact-message" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="e.g. ~50m² resin driveway, sub-base already in place" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button data-testid="public-contact-submit" onClick={submit} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <Mail className="w-4 h-4 mr-1" /> {submitting ? 'Sending…' : 'Send Request'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default function PublicInstallers() {
  const navigate = useNavigate();
  const [postcode, setPostcode] = useState('');
  const [section, setSection] = useState('');
  const [installers, setInstallers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contactTarget, setContactTarget] = useState(null);

  const load = async (pc = '', sec = '') => {
    setLoading(true);
    const params = new URLSearchParams();
    if (pc) params.set('postcode', pc);
    if (sec) params.set('section', sec);
    const res = await fetch(`${API_URL}/api/public/installers?${params.toString()}`);
    setInstallers(res.ok ? await res.json() : []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="text-white" style={{ background: 'linear-gradient(135deg, #064e3b, #065f46)' }}>
        <div className="max-w-6xl mx-auto px-4 py-10 text-center">
          <h1 className="text-3xl md:text-5xl font-bold flex items-center justify-center gap-3"><ShieldCheck className="w-10 h-10" /> Find an Ox Approved Installer</h1>
          <p className="text-white/80 mt-2">Every installer here is vetted, trained, photographed-from-start-to-finish, and uses genuine Ox Resin products.</p>

          <Card className="mt-6 p-3 bg-white/95 max-w-2xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_180px_120px] gap-2 items-center">
              <Input data-testid="public-search-postcode" value={postcode} onChange={(e) => setPostcode(e.target.value)} placeholder="Your postcode (e.g. OX1 1AA)" />
              <select data-testid="public-search-section" value={section} onChange={(e) => setSection(e.target.value)} className="border rounded-lg px-3 py-2">
                <option value="">Any service</option>
                <option value="probound">ProBound (Resin Bound)</option>
                <option value="proepoxy">ProEpoxy</option>
                <option value="prodecor">ProDecor</option>
              </select>
              <Button data-testid="public-search-btn" onClick={() => load(postcode, section)} className="bg-emerald-700 hover:bg-emerald-800 text-white">
                <Search className="w-4 h-4 mr-1" /> Search
              </Button>
            </div>
          </Card>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {loading ? (
          <Card className="p-10 text-center text-gray-500">Loading installers…</Card>
        ) : installers.length === 0 ? (
          <Card className="p-10 text-center text-gray-500">No approved installers found. Try a different postcode or service.</Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {installers.map(i => (
              <InstallerCard key={i.user_id} installer={i} onContact={setContactTarget} />
            ))}
          </div>
        )}
      </div>

      {contactTarget && <ContactModal installer={contactTarget} onClose={() => setContactTarget(null)} />}

      <div className="max-w-6xl mx-auto px-4 py-6 text-center">
        <Button variant="outline" onClick={() => navigate('/')}>← Back to Installer Portal</Button>
      </div>
    </div>
  );
}
